import * as loki from 'lokijs';
//import 'lokijs';
import {HttpClientManager} from 'wc-lib';
import {addMilliseconds} from 'date-fns';
import {convertToJson,parseJsonToObject,csvToArray, ResponseCodes} from 'wc-lib';
import Config from '../config';

export class Store {
  db: Loki = new loki('sandbox.db');
  endPoints: Array<StoreEndPoint> = [];
  dynamicViews: Array<CustomDynamicView<any>> = [];
  httpClient: any = HttpClientManager.httpClient();
  apiKeyValue: string = Config.apiKeyValue;
  apiurl: string = Config.baseReportingUrl;

  hashCode(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      let char = str.charCodeAt(i);
      hash = ((hash<<5)-hash)+char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString(16);
  }

  async addEndPoint(urlCall: string, body?: any, interval?: number, path?: string, type?: string, dateFormat?: string, toDateProperties?: string[]): Promise<string> {
    let url = this.apiurl+urlCall;    
    let parentId = this.hashCode(`${url}#${JSON.stringify(body)}`);
    if (path !== undefined) {
      if (this.endPointExists(parentId)) {
        this.updateInterval(parentId, interval); 
      } else {
        await this.addEndPoint(urlCall, body, interval, undefined, type, dateFormat, toDateProperties);
      }
    }
    let id = `${parentId}${path !== undefined ? `#${path}` : ''}`;
    let endPoint: StoreEndPoint = {
      id: id,
      url: url,
      body: body,
      interval: interval,
      path: path,
      type: type,
      dateFormat: dateFormat,
      toDateProperties: toDateProperties
    };

    if(!this.endPointExists(id)) {
      this.db.addCollection(id);
      await this.retrieveData(endPoint);
      if(!this.endPointExists(id)) {
        this.endPoints.push(endPoint);
      }
    } else {
      this.updateInterval(id, interval); 
      await this.retrieveData(endPoint);
    }

    return id;
  }

  endPointExists(id: string): boolean {
    return this.endPoints.find(x => x.id === id) !== undefined;
  }

  updateInterval(id: string, interval?: number) {
    if (interval !== undefined) {
      let endPoint = this.endPoints.find(x => x.id === id);
      if (endPoint.interval === undefined) {
        endPoint.interval = interval;
      } else if (endPoint.interval > interval) {
        endPoint.interval = interval;
      }
    }
  }

  getParentId(endPoint: StoreEndPoint): string {
    return this.hashCode(`${endPoint.url}#${JSON.stringify(endPoint.body)}`);
  }

  async retrieveData(endPoint: StoreEndPoint) {
    let now = new Date();
    if (endPoint.path !== undefined) {
      //check if parent data has been retrieved recently
      let parentEndPoint = this.endPoints.find(x => x.id == this.getParentId(endPoint));
      if (parentEndPoint.interval && parentEndPoint.lastRetrieval < addMilliseconds(now, -parentEndPoint.interval)) {
        await this.retrieveDataRemotely(parentEndPoint);
      }
      this.retrieveDataFromExistingCollection(endPoint);
    } else {
      let parentEndPoint = this.endPoints.find(x => x.id === endPoint.id) || endPoint;
      if (!parentEndPoint.lastRetrieval || (parentEndPoint.interval && parentEndPoint.lastRetrieval < addMilliseconds(now, -parentEndPoint.interval))) {
        await this.retrieveDataRemotely(parentEndPoint);
      }
    }
  }

  retrieveDataFromExistingCollection(endPoint: StoreEndPoint) {
    let rawId = this.hashCode(`${endPoint.url}#${JSON.stringify(endPoint.body)}`);
    let rawData = this.getCollection(rawId).data[0];
    let currObject = rawData;

    let paths = endPoint.path.split('.');
    for(let path of paths) {
      currObject = currObject[path];
    }

    this.addData(endPoint.id, currObject);
  }

  async retrieveDataRemotely(endPoint: StoreEndPoint) {
    let contentType: string;
    endPoint.lastRetrieval = new Date();
    switch (typeof endPoint.body) {
      case 'string':
        contentType = 'text/plain';
        break;
      case 'object':
      default:
        contentType = 'application/json';
    }
    let response;
    let jsonResponse;
    let text;
    
    if (endPoint.body !== undefined) {
      response = await this.httpClient.fetch(endPoint.url, {
        method: 'POST',
        headers: { 
          'content-type': contentType,
          'X-DC-DEVKEY' : this.apiKeyValue
       },
        body: convertToJson(endPoint.body),
      });
    } else {
      response = await this.httpClient.fetch(endPoint.url, {
        method: 'GET',
        headers: { 
          'content-type': contentType,
          'X-DC-DEVKEY' : this.apiKeyValue
       }
      });
    }
    if (response.status === ResponseCodes.success) {
      text = await response.text();
      let toDateProperties = endPoint.toDateProperties ? endPoint.toDateProperties : [];
      switch (endPoint.type) {
        case 'csv':
          jsonResponse = csvToArray(text, null, endPoint.dateFormat, ...toDateProperties);
          break;
        case undefined:
        default:
          jsonResponse = parseJsonToObject(text, ...toDateProperties);
          break;
      }
  
      this.addData(endPoint.id, jsonResponse);
    }
  }
  
  getCollection(id: string) : Collection<any> {
    return this.db.getCollection(id);
  }

  addData(id: string, data: any) : Collection<any>  {
    if (Array.isArray(data)) {
      data.map(x => { delete x['$loki']; delete x['meta']; });
    } else { 
      if (data['$loki']) {
        delete data['$loki'];
      }
      if (data['meta']) {
        delete data['meta'];
      }
    }
    let collection = this.db.getCollection(id);
    collection.clear();
    collection.insert(data);
    return collection;
  }

  getDynamicView(collectionId: string, dynamicViewId: string): CustomDynamicView<any> {
    let lokiCollection = this.db.getCollection(collectionId);
    let dynamicView = lokiCollection.getDynamicView(dynamicViewId) as CustomDynamicView<any>;
    if (dynamicView === null) {
      dynamicView = lokiCollection.addDynamicView(dynamicViewId) as CustomDynamicView<any>;
      let manager = this;
      dynamicView.getData = async function() {
        let endPoint = manager.endPoints.find(x => x.id === collectionId);
        await manager.retrieveData(endPoint);
        return this.data();
      }
      this.dynamicViews.push(dynamicView);
    }

    return dynamicView;
  }
}

class StoreEndPoint {
  id: string;
  url: string;
  body?: any;
  interval?: number;
  path?: string;
  setInterval?: number;
  type?: string;
  dateFormat?: string;
  toDateProperties?: string[];
  lastRetrieval?: Date;
}

class CustomGetData {
  getData: Function;
}

export type CustomDynamicView<T extends object> = CustomGetData & DynamicView<T>;

export let DataManager = new Store();



// WEBPACK FOOTER //
// src/store/store-core.ts